// Orders a restaurant took outside the marketplace, shaped like model.js's
// toView so they sit on a restaurant's board with its own orders and open in
// the same drawer: old-app orders mirrored from the legacy RushMyFood app
// (eHungry), and email orders parsed from a partner's order emails (courier
// orders).
//
// `external` makes every dispatch action (assign, status moves, bulk cancel,
// send to First Delivery) refuse them — none of our order mutations accept
// their ids. What the drawer needs that toView reads off the raw order lives on
// the view instead: `items` (in the marketplace item shape), `payment`,
// `trackerLinks` and `proofPhoto`. Ids are namespaced because both tables count
// from 1 like `orders` does.
//
// Every delivery here is carried by First Delivery. An old-app delivery reads
// "RushMyFood (old)" on both boards; an email one is First Delivery's, which a
// restaurant's board folds into "RushMyFood" (restaurants never see FD by name).

import { MIN, money, ms, shortAddress } from './model'
import { ehungryDueAt, isScheduledEhungryOrder } from '../Order/EhungryOrderCard'
import { courierPrintDueAt, isScheduledCourierOrder } from '../Order/EmailOrderCard'

export const OLD_APP_LABEL = 'RushMyFood (old)'
export const EMAIL_LABEL = 'Email order'

const byTime = h => h.filter(x => x.at).sort((a, b) => a.at - b.at)
const point = (lat, lng) => (lat != null && lng != null && !isNaN(lat) && !isNaN(lng) ? { lat: Number(lat), lng: Number(lng) } : null)
const amount = n => (n == null ? 0 : Number(n) || 0)

/** `home` is the order's restaurant as our own orders know it (logo, phone, map pin); the order's own details are the fallback. */
function restaurantFor(home, name, location, address) {
  return {
    id: home?.id || null,
    name: home?.name || name || '',
    image: home?.image || null,
    address: home?.address || address || null,
    phone: home?.phone || null,
    location: home?.location || location || null,
    zoneId: home?.zoneId || null,
    // The restaurant's clock when it's known; partner orders otherwise keep the browser's.
    timezone: home?.timezone || null,
  }
}

/**
 * Where an old-app order is. The legacy app only knows pending / completed /
 * cancelled, so an open delivery follows its courier's own stages instead.
 */
function ehungryStatus(o, isPickup, now) {
  if (o.status === 'cancelled') return 'CANCELLED'
  if (o.status === 'completed') return isPickup ? 'COLLECTED' : 'DELIVERED'
  const c = o.courier
  if (!isPickup && c && !c.cancelledAt) {
    if (c.droppedOffAt) return 'DELIVERED'
    if (c.arrivedDropoffAt) return 'ARRIVED_DROPOFF'
    if (c.pickedUpAt) return 'PICKED'
    if (c.arrivedOriginAt) return 'ARRIVED_PICKUP'
    if (c.assignedAt || c.acceptedAt || c.driverName) return 'ASSIGNED'
  }
  // Same rule as the Orders page and the tablet: a pre-order waits until its ticket prints.
  if (isScheduledEhungryOrder(o) && now < ehungryDueAt(o)) return 'SCHEDULED'
  return 'ACCEPTED'
}

/** An old-app line in the marketplace item shape the drawer lists: size as the variation, choices as options. */
const ehungryItem = (it, i) => ({
  _id: `eh-${i}`,
  title: `${it.name || 'Item'}${it.forGuest ? ` (${it.forGuest})` : ''}`,
  quantity: it.quantity || 1,
  variation: { title: it.variant || null, price: it.unitPrice ?? (it.totalPrice != null && it.quantity ? it.totalPrice / it.quantity : null) },
  addons: [{
    options: (it.addons || []).map((a, j) => ({
      _id: `eh-${i}-${j}`,
      // "Plate Sides (Korean BBQ Wings)" — the group alone says nothing about what was ordered.
      title: `${a.choice ? `${a.name} (${a.choice})` : a.name}${a.portion ? ` — ${a.portion}` : ''}`,
      price: a.price,
    })),
  }],
  specialInstructions: it.specialInstructions || null,
})

export function ehungryToView(o, home, now) {
  const isDelivery = o.orderType === 'DELIVERY'
  const type = isDelivery ? 'DELIVERY' : 'PICKUP'
  const typeLabel = o.orderType === 'DINEIN' ? 'Dine-in' : isDelivery ? 'Delivery' : 'Pickup'
  const status = ehungryStatus(o, !isDelivery, now)
  const c = isDelivery ? o.courier : null
  const placedAt = ms(o.placedAt) || 0
  const readyAt = ms(o.readyAt)
  const pickedUpAt = ms(c?.pickedUpAt)
  const droppedOffAt = ms(c?.droppedOffAt)
  const finished = status === 'DELIVERED' || status === 'COLLECTED'
  const deliveredAt = status === 'DELIVERED' ? droppedOffAt || ms(o.processedAt) : null
  const cancelledAt = status === 'CANCELLED' ? ms(c?.cancelledAt) || ms(o.processedAt) : null

  const driver = c?.driverName
    ? { id: null, name: c.driverName, phone: c.driverPhone || null, photo: c.driverPhoto || null, kind: 'RUSHMYFOOD', transport: c.driverTransport || null }
    : null

  let pickup = null
  if (!isDelivery && readyAt) pickup = { at: readyAt, done: finished }
  else if (pickedUpAt) pickup = { at: pickedUpAt, done: true }
  else if (ms(c?.pickupEstimate)) pickup = { at: ms(c.pickupEstimate), done: false }
  else if (readyAt) pickup = { at: readyAt, done: false }

  let dropoff = null
  if (isDelivery) {
    if (deliveredAt) dropoff = { at: deliveredAt, done: true }
    else if (ms(c?.dropoffEstimate)) dropoff = { at: ms(c.dropoffEstimate), done: false }
  }

  const active = !['DELIVERED', 'COLLECTED', 'CANCELLED'].includes(status)
  return {
    raw: o,
    id: `eh:${o._id}`,
    external: 'EHUNGRY',
    sourceLabel: OLD_APP_LABEL,
    orderNo: String(o.remoteId || o._id),
    status,
    type,
    typeLabel,
    fulfilment: o.orderType === 'DINEIN' ? 'DINE_IN' : isDelivery ? 'RUSHMYFOOD' : 'PICKUP',
    channel: isDelivery ? 'RUSHMYFOOD_OLD' : 'UNASSIGNED',
    driver,
    awaitingCourier: isDelivery && active && !driver,
    restaurant: restaurantFor(home, o.restaurantName, null),
    customer: o.customerName || o.customerPhone ? { name: o.customerName || o.customerPhone, phone: o.customerPhone || null } : null,
    address: isDelivery && o.deliveryAddress
      ? {
          full: o.deliveryAddress,
          short: shortAddress(o.deliveryAddress),
          details: [o.deliveryNote, o.notesToDriver, o.specialInstructions].filter(Boolean).join(' · '),
          // The legacy app keeps no coordinates; the drawer looks the address up.
          location: null,
        }
      : null,
    placedAt,
    pickup,
    dropoff,
    deliveredAt,
    collectedAt: status === 'COLLECTED' ? ms(o.processedAt) : null,
    cancelledAt,
    scheduledFor: status === 'SCHEDULED' ? readyAt : null,
    total: amount(o.total),
    tip: amount(o.tip),
    history: byTime([
      { status: 'PENDING', at: placedAt },
      { status: 'ASSIGNED', at: ms(c?.assignedAt) },
      { status: 'ARRIVED_PICKUP', at: ms(c?.arrivedOriginAt) },
      { status: 'PICKED', at: pickedUpAt },
      { status: 'ARRIVED_DROPOFF', at: ms(c?.arrivedDropoffAt) },
      { status: 'DELIVERED', at: deliveredAt },
      { status: 'CANCELLED', at: cancelledAt },
    ]),
    items: (o.items || []).map(ehungryItem),
    payment: {
      lines: [
        ['Subtotal', money(o.subtotal)],
        amount(o.discount) > 0 && ['Discount', `-${money(o.discount)}`, 'credit'],
        amount(o.couponAmount) > 0 && [o.couponName || 'Coupon', `-${money(o.couponAmount)}`, 'credit'],
        [o.taxLabel || 'Tax', money(o.tax)],
        ['Delivery', money(o.deliveryCharge)],
        amount(o.additionalFee) > 0 && ['Service fee', money(o.additionalFee)],
        ['Tip', money(o.tip)],
      ].filter(Boolean),
      // Stored as HTML by the legacy app ("…5369<br />Name: …").
      caption: o.paymentDescription ? o.paymentDescription.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').trim() : null,
    },
    // First Delivery's tracker, then the delivery service's own (DoorDash, Grubhub…).
    trackerLinks: [c?.trackerLink, c?.dspTrackerLink].filter(Boolean),
    proofPhoto: c?.dropoffPhoto || null,
    reason: null,
  }
}

/**
 * Where an email order is — the same buckets the Orders page files it under,
 * except a delivery the driver has collected is on its way, not done. null
 * keeps it off the board (NEEDS_REVIEW and the like: not an order the kitchen
 * can act on yet).
 */
function courierStatus(o, isPickup, now) {
  switch (o.status) {
    case 'CANCELLED':
    case 'FAILED':
      return 'CANCELLED'
    case 'DELIVERED':
      return isPickup ? 'COLLECTED' : 'DELIVERED'
    case 'PICKED_UP':
      return isPickup ? 'COLLECTED' : 'PICKED'
    case 'EN_ROUTE':
      return 'EN_ROUTE_DROPOFF'
    case 'ASSIGNED':
      return 'ASSIGNED'
    case 'SCHEDULED':
      return 'SCHEDULED'
    case 'DRAFT':
    case 'READY':
    case 'DISPATCHING':
      return isScheduledCourierOrder(o) && now < courierPrintDueAt(o) ? 'SCHEDULED' : 'ACCEPTED'
    default:
      return null
  }
}

const courierItem = it => {
  let mods = []
  try { mods = it.modifiers ? JSON.parse(it.modifiers) : [] } catch (e) { mods = [] }
  return {
    _id: `c-${it._id}`,
    title: it.title || 'Item',
    quantity: it.quantity || 1,
    variation: { title: null, price: it.unitPrice },
    addons: [{ options: (Array.isArray(mods) ? mods : []).map((m, j) => ({ _id: `c-${it._id}-${j}`, title: m.title, price: m.price ?? 0 })) }],
    specialInstructions: null,
  }
}

export function courierToView(o, home, now) {
  const isDelivery = o.orderKind === 'DELIVERY'
  const type = isDelivery ? 'DELIVERY' : 'PICKUP'
  const typeLabel = o.orderKind === 'DINE_IN' ? 'Dine-in' : isDelivery ? 'Delivery' : 'Pickup'
  const status = courierStatus(o, !isDelivery, now)
  const placedAt = ms(o.createdAt) || 0
  const promise = ms(o.promiseTime)
  // The last word from First Delivery on a delivered order is the dropoff.
  const lastEvent = ms(o.lastFdEventAt)
  const onTheWay = ['PICKED', 'EN_ROUTE_DROPOFF', 'DELIVERED'].includes(status)

  const driver = isDelivery && o.fdDriverName
    ? { id: null, name: o.fdDriverName, phone: o.fdDriverPhone || null, photo: null, kind: 'RUSHMYFOOD', transport: o.fdDriverTransport || null }
    : null

  let pickup = null
  if (!isDelivery && promise) pickup = { at: promise, done: status === 'COLLECTED' }
  else if (ms(o.fdPickupEta)) pickup = { at: ms(o.fdPickupEta), done: onTheWay }

  let dropoff = null
  if (isDelivery) {
    if (status === 'DELIVERED') dropoff = { at: lastEvent || ms(o.fdDropoffEta) || promise || placedAt + 45 * MIN, done: true }
    else if (ms(o.fdDropoffEta)) dropoff = { at: ms(o.fdDropoffEta), done: false }
    else if (promise) dropoff = { at: promise, done: false }
  }
  const deliveredAt = status === 'DELIVERED' ? dropoff?.at || null : null
  const cancelledAt = status === 'CANCELLED' ? lastEvent : null

  const active = status && !['DELIVERED', 'COLLECTED', 'CANCELLED'].includes(status)
  return {
    raw: o,
    id: `c:${o._id}`,
    external: 'EMAIL',
    sourceLabel: EMAIL_LABEL,
    orderNo: String(o.externalOrderNumber || o._id),
    status,
    type,
    typeLabel,
    fulfilment: o.orderKind === 'DINE_IN' ? 'DINE_IN' : isDelivery ? 'FIRST_DELIVERY' : 'PICKUP',
    channel: isDelivery ? 'FIRST_DELIVERY' : 'UNASSIGNED',
    driver,
    awaitingCourier: isDelivery && !!active && !driver,
    restaurant: restaurantFor(home, o.merchantName, point(o.merchantLatitude, o.merchantLongitude), o.merchantAddress),
    customer: o.customerName || o.customerPhone ? { name: o.customerName || o.customerPhone, phone: o.customerPhone || null } : null,
    address: isDelivery && o.dropoffAddress
      ? { full: o.dropoffAddress, short: shortAddress(o.dropoffAddress), details: '', location: point(o.dropoffLatitude, o.dropoffLongitude) }
      : null,
    placedAt,
    pickup,
    dropoff,
    deliveredAt,
    collectedAt: status === 'COLLECTED' ? lastEvent : null,
    cancelledAt,
    scheduledFor: status === 'SCHEDULED' ? promise : null,
    total: amount(o.total),
    tip: amount(o.tip),
    history: byTime([
      { status: 'PENDING', at: placedAt },
      { status: 'DELIVERED', at: deliveredAt },
      { status: 'CANCELLED', at: cancelledAt },
    ]),
    items: (o.items || []).map(courierItem),
    payment: {
      lines: [
        ['Subtotal', money(o.subtotal)],
        ['Delivery', money(o.deliveryFee)],
        ['Processing fee', money(o.processingFee)],
        ['Tax', money(o.tax)],
        ['Tip', money(o.tip)],
      ],
      // null is genuinely unknown (no payment line on the receipt), not the same as unpaid.
      caption: o.isPaid == null ? 'Payment status unknown' : `${o.isPaid ? 'Paid' : 'Unpaid'}${o.paymentMethod ? ` · ${o.paymentMethod}` : ''}`,
    },
    trackerLinks: [o.fdTrackerLink, o.fdDspTrackerLink].filter(Boolean),
    proofPhoto: o.fdDropoffPhoto || null,
    reason: status === 'CANCELLED' ? o.failureReason || null : null,
  }
}
